import { useState } from "react";
import { Workforce, WorkforceStatus, StatusChangeReason, WORKFORCE_STATUSES } from "@/types/workforce";
import { WorkforceKanbanColumn } from "./WorkforceKanbanColumn";
import { WorkforceStatusChangeModal, BenchCreationData } from "./WorkforceStatusChangeModal";
import { WorkforceEditModal } from "./WorkforceEditModal";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { useTranslation } from "react-i18next";

interface WorkforceKanbanProps {
	workforce: Workforce[];
	onStatusChange?: () => void;
}

interface PendingDrop {
	workforceId: string;
	currentStatus: WorkforceStatus;
	targetStatus: WorkforceStatus;
}

export const WorkforceKanban = ({ workforce, onStatusChange }: WorkforceKanbanProps) => {
	const { t } = useTranslation();
	const [isUpdating, setIsUpdating] = useState<string | null>(null);
	const [pendingDrop, setPendingDrop] = useState<PendingDrop | null>(null);
	const [editingId, setEditingId] = useState<string | null>(null);

	// Get the workforce item for the pending drop
	const pendingItem = pendingDrop ? workforce.find(w => w.id === pendingDrop.workforceId) : null;

	// Group workforce by status
	const groupedWorkforce = WORKFORCE_STATUSES.reduce((acc, status) => {
		acc[status.value] = workforce.filter(w => w.status === status.value);
		return acc;
	}, {} as Record<WorkforceStatus, Workforce[]>);

	const handleDrop = (workforceId: string, newStatus: WorkforceStatus) => {
		const item = workforce.find(w => w.id === workforceId);
		if (!item || item.status === newStatus) return;

		// Open modal instead of directly updating
		setPendingDrop({
			workforceId,
			currentStatus: item.status,
			targetStatus: newStatus,
		});
	};

	const handleModalConfirm = async (data: {
		status: WorkforceStatus;
		end_date?: string;
		status_change_reason?: StatusChangeReason;
		status_change_note?: string;
		createBench?: BenchCreationData;
	}) => {
		if (!pendingDrop) return;

		const droppedItem = workforce.find(w => w.id === pendingDrop.workforceId);
		setIsUpdating(pendingDrop.workforceId);
		setPendingDrop(null);

		try {
			const updateData: any = { status: data.status };

			if (data.end_date)
				updateData.end_date = data.end_date;
			else if (data.status === 'completed' || data.status === 'terminated')
				updateData.end_date = new Date().toISOString().split('T')[0];

			if (data.status_change_reason)
				updateData.status_change_reason = data.status_change_reason;

			if (data.status_change_note)
				updateData.status_change_note = data.status_change_note;

			const { error } = await supabase
				.from('workforce')
				.update(updateData)
				.eq('id', droppedItem?.id || pendingDrop.workforceId);

			if (error) throw error;

			toast({
				title: t("workforce.statusUpdated", "Status updated"),
				description: t("workforce.statusChangedTo", "Assignment status changed"),
			});

			if (onStatusChange) onStatusChange();

			// Create bench assignment if requested
			if (data.createBench && droppedItem?.candidate_id) {
				try {
					// Check if a bench entry already exists from this workforce
					const { data: existing } = await supabase
						.from('workforce')
						.select('id')
						.eq('created_from', droppedItem.id)
						.eq('is_bench', true)
						.limit(1);

					if (existing && existing.length > 0) {
						toast({
							title: t("workforce.bench.errors.alreadyOnBench", "Already on bench"),
							description: t("workforce.bench.errors.benchAlreadyCreated", "A bench assignment was already created from this assignment"),
							variant: "destructive",
						});
					} else {
						const { error: insertError } = await supabase.from('workforce').insert({
							organization_uuid: droppedItem.organization_uuid,
							candidate_id: droppedItem.candidate_id,
							start_date: new Date().toISOString().split('T')[0],
							status: 'active',
							is_bench: true,
							bench_daily_cost: data.createBench.bench_daily_cost,
							currency: data.createBench.currency,
							bench_reason: data.createBench.bench_reason || null,
							work_percentage: data.createBench.work_percentage || null,
							notes: data.createBench.notes || null,
							previous_workforce_id: droppedItem.id,
							created_from: droppedItem.id,
						}).select('id').single();

						if (insertError) throw insertError;

						toast({
							title: t("workforce.bench.addSuccess", "Added to bench"),
							description: t("workforce.bench.addSuccessDesc", "Consultant has been added to the bench"),
						});
					}
				} catch (benchError: any) {
					toast({
						title: t("common.error", "Error"),
						description: benchError?.message || t("workforce.bench.errors.addFailed", "Failed to add to bench"),
						variant: "destructive",
					});
				}
			}
		} catch {
			toast({
				title: t("workforce.updateError", "Error updating status"),
				description: t("workforce.updateErrorDesc", "Could not update the assignment status"),
				variant: "destructive",
			});
		} finally {
			setIsUpdating(null);
		}
	};

	return (
		<>
			<div className="flex space-x-4 overflow-x-auto pb-4">
				{WORKFORCE_STATUSES.map((status) => (
					<WorkforceKanbanColumn
						key={status.value}
						status={status.value}
						label={status.label}
						items={groupedWorkforce[status.value] || []}
						onDrop={handleDrop}
						isUpdating={isUpdating}
						onEdit={setEditingId}
					/>
				))}
			</div>

			<WorkforceEditModal
				workforceId={editingId}
				onOpenChange={(open) => { if (!open) setEditingId(null); }}
				onSuccess={onStatusChange}
			/>

			{pendingDrop && pendingItem && (
				<WorkforceStatusChangeModal
					isOpen={!!pendingDrop}
					onClose={() => setPendingDrop(null)}
					currentStatus={pendingDrop.currentStatus}
					targetStatus={pendingDrop.targetStatus}
					candidateName={pendingItem.candidate?.name}
					candidateId={pendingItem.candidate_id}
					workforceId={pendingItem.id}
					currentAllocation={pendingItem.work_percentage}
					currentCurrency={pendingItem.currency}
					isBench={pendingItem.is_bench}
					onConfirm={handleModalConfirm}
				/>
			)}
		</>
	);
};
